/**
 * Session cache for Discord-style NEW dividers.
 * Anchors survive room switches / remounts / refresh until the user types in the
 * composer (dismiss advances the cursor to the newest message so the same unread
 * batch does not reappear).
 */

export type NewDividerAnchor = {
  lastSeenTime: number | null;
  lastSeenMessageId: string | null;
  dismissed: boolean;
};

const ROOM_STORAGE_KEY = 'mp.newDivider.rooms';
const THREAD_STORAGE_KEY = 'mp.newDivider.threads';

const roomAnchors = new Map<string, NewDividerAnchor>();
const threadAnchors = new Map<string, NewDividerAnchor>();

let roomsHydrated = false;
let threadsHydrated = false;

const threadKey = (roomId: string | number, parentId: string | number) =>
  `${roomId}:${parentId}`;

const readStorage = (key: string): Record<string, NewDividerAnchor> => {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeStorage = (key: string, map: Map<string, NewDividerAnchor>): void => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const obj: Record<string, NewDividerAnchor> = {};
    map.forEach((value, k) => {
      obj[k] = value;
    });
    sessionStorage.setItem(key, JSON.stringify(obj));
  } catch {
    // Quota / private mode — in-memory still works for the current page life.
  }
};

const hydrateRooms = (): void => {
  if (roomsHydrated) return;
  roomsHydrated = true;
  const stored = readStorage(ROOM_STORAGE_KEY);
  Object.entries(stored).forEach(([k, v]) => {
    if (v && typeof v === 'object') roomAnchors.set(k, v);
  });
};

const hydrateThreads = (): void => {
  if (threadsHydrated) return;
  threadsHydrated = true;
  const stored = readStorage(THREAD_STORAGE_KEY);
  Object.entries(stored).forEach(([k, v]) => {
    if (v && typeof v === 'object') threadAnchors.set(k, v);
  });
};

const persistRooms = (): void => writeStorage(ROOM_STORAGE_KEY, roomAnchors);
const persistThreads = (): void => writeStorage(THREAD_STORAGE_KEY, threadAnchors);

/** Prefer the later of two last-seen cursors (by time; message id as tie-break). */
const mergeCursor = (
  a: Pick<NewDividerAnchor, 'lastSeenTime' | 'lastSeenMessageId'>,
  b: Pick<NewDividerAnchor, 'lastSeenTime' | 'lastSeenMessageId'>
): Pick<NewDividerAnchor, 'lastSeenTime' | 'lastSeenMessageId'> => {
  const aTime = typeof a.lastSeenTime === 'number' ? a.lastSeenTime : null;
  const bTime = typeof b.lastSeenTime === 'number' ? b.lastSeenTime : null;
  if (aTime == null && bTime == null) {
    return {
      lastSeenTime: null,
      lastSeenMessageId: a.lastSeenMessageId ?? b.lastSeenMessageId ?? null,
    };
  }
  if (aTime == null) return { lastSeenTime: bTime, lastSeenMessageId: b.lastSeenMessageId };
  if (bTime == null) return { lastSeenTime: aTime, lastSeenMessageId: a.lastSeenMessageId };
  if (aTime > bTime) return { lastSeenTime: aTime, lastSeenMessageId: a.lastSeenMessageId };
  if (bTime > aTime) return { lastSeenTime: bTime, lastSeenMessageId: b.lastSeenMessageId };
  return {
    lastSeenTime: aTime,
    lastSeenMessageId: a.lastSeenMessageId ?? b.lastSeenMessageId ?? null,
  };
};

export const getRoomNewDividerAnchor = (
  roomId: string | number | null | undefined
): NewDividerAnchor | undefined => {
  if (roomId == null) return undefined;
  hydrateRooms();
  return roomAnchors.get(String(roomId));
};

/**
 * Store anchor once per room visit until dismissed.
 * Does not overwrite an active (non-dismissed) anchor.
 * After dismiss, merges the advanced cursor with Firebase lastSeen and clears
 * dismissed so the same unread batch stays gone across refresh, while newer
 * messages can still show a NEW bar.
 */
export const ensureRoomNewDividerAnchor = (
  roomId: string | number,
  lastSeenTime: number | null,
  lastSeenMessageId: string | null
): NewDividerAnchor => {
  hydrateRooms();
  const key = String(roomId);
  const existing = roomAnchors.get(key);
  if (existing && !existing.dismissed) return existing;

  const incoming = { lastSeenTime, lastSeenMessageId };
  const cursor = existing?.dismissed ? mergeCursor(existing, incoming) : incoming;

  const next: NewDividerAnchor = {
    lastSeenTime: cursor.lastSeenTime,
    lastSeenMessageId: cursor.lastSeenMessageId,
    dismissed: false,
  };
  roomAnchors.set(key, next);
  persistRooms();
  return next;
};

/**
 * Hide the NEW bar and advance the divider cursor to the newest message so the
 * same unread batch does not return after remount / refresh.
 */
export const dismissRoomNewDivider = (
  roomId: string | number | null | undefined,
  newestLastSeenTime?: number | null,
  newestLastSeenMessageId?: string | null
): void => {
  if (roomId == null) return;
  hydrateRooms();
  const key = String(roomId);
  const existing = roomAnchors.get(key);
  if (!existing || existing.dismissed) return;

  const advanced = mergeCursor(existing, {
    lastSeenTime: newestLastSeenTime ?? existing.lastSeenTime,
    lastSeenMessageId: newestLastSeenMessageId ?? existing.lastSeenMessageId,
  });

  roomAnchors.set(key, {
    lastSeenTime: advanced.lastSeenTime,
    lastSeenMessageId: advanced.lastSeenMessageId,
    dismissed: true,
  });
  persistRooms();
};

export const getThreadNewDividerAnchor = (
  roomId: string | number | null | undefined,
  parentId: string | number | null | undefined
): NewDividerAnchor | undefined => {
  if (roomId == null || parentId == null) return undefined;
  hydrateThreads();
  return threadAnchors.get(threadKey(roomId, parentId));
};

export const ensureThreadNewDividerAnchor = (
  roomId: string | number,
  parentId: string | number,
  lastSeenTime: number | null
): NewDividerAnchor => {
  hydrateThreads();
  const key = threadKey(roomId, parentId);
  const existing = threadAnchors.get(key);
  if (existing && !existing.dismissed) return existing;

  const incoming = { lastSeenTime, lastSeenMessageId: null as string | null };
  const cursor = existing?.dismissed ? mergeCursor(existing, incoming) : incoming;

  const next: NewDividerAnchor = {
    lastSeenTime: cursor.lastSeenTime,
    lastSeenMessageId: null,
    dismissed: false,
  };
  threadAnchors.set(key, next);
  persistThreads();
  return next;
};

export const dismissThreadNewDivider = (
  roomId: string | number | null | undefined,
  parentId: string | number | null | undefined,
  newestLastSeenTime?: number | null
): void => {
  if (roomId == null || parentId == null) return;
  hydrateThreads();
  const key = threadKey(roomId, parentId);
  const existing = threadAnchors.get(key);
  if (!existing || existing.dismissed) return;

  const advanced = mergeCursor(existing, {
    lastSeenTime: newestLastSeenTime ?? existing.lastSeenTime,
    lastSeenMessageId: null,
  });

  threadAnchors.set(key, {
    lastSeenTime: advanced.lastSeenTime,
    lastSeenMessageId: null,
    dismissed: true,
  });
  persistThreads();
};
